import { Component, EventEmitter, Input, OnInit, Output, SimpleChanges, OnChanges, OnDestroy } from '@angular/core';
import { FormGroup, FormBuilder, FormControl, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MIN_SEARCH_CHARS, SEARCH_DEBOUNCE_MILLIS } from 'src/app/constants/list';
import { MasterService } from 'src/app/services/master.service';
import { MessageService } from 'src/app/services/message.service';
import * as words from '../../../../services/language'
import { ISSUERS } from 'src/app/constants/contact';
import { SCROLL_BOTTOM_OUT } from 'src/app/constants/list';
import { Address } from 'src/app/models/address.model';
import { ContactRole } from 'src/app/models/contact-role.model';
import { IdentificationIssuer } from 'src/app/models/identification-issuer.model';
import { Subscription } from 'rxjs';

export interface Contact {
    name: string;
    id: number;
}
@Component({
    selector: 'contact-advanced-search',
    templateUrl: './contact-advanced-search.component.html',
    styleUrls: ['./contact-advanced-search.component.scss'],
    standalone: false
})

export class ContactAdvancedSearchComponent implements OnInit, OnChanges, OnDestroy {

    // Current type filter value.
    @Input() calledFrom: string = "contactAdvancedSearch"

    // Current type filter value.
    @Input() type: string | null = null

    // In order to have multiple comps on a single page we need unique input identities. This key will be appended to the id attrbute to ensure that uniqueness.
    @Input() key: string | null = null

    // Current disabled value.
    @Input() isDisabled: boolean = false

    // Current saved value in the database
    @Input() contactIdSaved: number;

    // Current saved value in the database
    @Input() contactNameSaved: string;

    /** Indicates whether ot not the label should show for the input. (default: true) */
    @Input() displayLabel: boolean = true;

    // Send the child the contact id selected
    @Output() setContactId: EventEmitter<Number> = new EventEmitter()

    public typesWithNoSetup: string[] = ['businessContact', 'salesLead', 'inventorySource', 'vendor', 'autoInsurance', 'warranty'];

    // Current selected value in front end
    public contactIdSelected: number;

    // The current search text to filter the list by.
    public inquiry: string | null = null

    public contacts: Contact[] = [];

    // The search form group
    public form: FormGroup | null = null

    // A debounce timer for search events. Used to wait for 
    // the user to stop typing before we fetch.
    private watcher: ReturnType<typeof setTimeout> = null

    // Current page the user is viewing (last loaded).
    public page = 1

    // The number contacts currently being displayed.
    public displayed = 0

    // Indicates whether or not there are no more records to load.
    public exhausted = false

    // define if is loading
    public loading: boolean = false;

    // Keep track of how the user wants to sort list.
    private sortables: Object = { 'name': 'ASC' }

    private subscriptions: Subscription[] = []

    // save the language
    public language: string = localStorage.getItem('language') ? localStorage.getItem('language') : 'EN'

    // set all words
    public words = words.language

    // Total number of records available
    public records: number

    public ignoreType: boolean = false;

    public contactCreated: boolean = false;

    public enterClicked: boolean;

    public showSaveButton: boolean = false;

    public zipCodeList: Address[] = []

    // A debounce timer for zip search events. Used to wait for 
    // the user to stop typing before we fetch.
    private zipWatcher: ReturnType<typeof setTimeout> = null

    public types: ContactRole[] = []

    // latest search inquiry
    public zipInquiry = null

    public issuers: IdentificationIssuer[] = ISSUERS

    public contactQuickAddInfo = new FormGroup({
        id: new FormControl<number | null>(null),
        isIndividual: new FormControl<string | null>(null, [Validators.required]),
        taxPayerNumber: new FormControl<string | null>(null),
        identificationTypeId: new FormControl<number | null>(null),
        number: new FormControl<string | null>(null),
        issuer: new FormControl<string | null>(null),
        expirationDate: new FormControl<string | null>(null),
        firstName: new FormControl<string | null>(null),
        lastName: new FormControl<string | null>(null),
        middleName: new FormControl<string | null>(null),
        companyName: new FormControl<string | null>(null),
        address: new FormControl<string | null>(null),
        address2: new FormControl<string | null>(null),
        city: new FormControl<string | null>({ value: null, disabled: true }),
        state: new FormControl<string | null>({ value: null, disabled: true }),
        zipCode: new FormControl<string | null>(null, [Validators.minLength(5),]),
        county: new FormControl<string | null>({ value: null, disabled: true }),
        country: new FormControl<string | null>({ value: null, disabled: true }),
        phoneNumber: new FormControl<string | null>(null),
        emailAddress: new FormControl<string | null>(null),
        roles: new FormControl<string | null>(null, [Validators.required])
    })

    get isIndividual(): boolean { return this.contactQuickAddInfo.get('isIndividual').value == 'true' }

    constructor(private ms: MessageService, private fb: FormBuilder, private master: MasterService) {
        this.form = this.fb.group({
            inquiry: new FormControl<string | null>(null, [Validators.required])
        })
    }

    ngOnChanges(changes: SimpleChanges): void {
        //Called before any other lifecycle hook. Use it to inject dependencies, but avoid any serious work here.
        //Add '${implements OnChanges}' to the class.
        if (!changes) {
            return
        }

        if (changes.isDisabled) {
            this.form.get('inquiry').enable()
            if (this.isDisabled) {
                this.form.get('inquiry').disable()
            }
        }

        if (changes.contactNameSaved) {
            this.form.get('inquiry').setValue(changes.contactNameSaved.currentValue)
            this.inquiry = changes.contactNameSaved.currentValue
        }

        if (changes.contactIdSaved) {
            this.contactIdSelected = this.contactIdSaved
            if (!this.contactIdSaved) {
                this.form.get('inquiry').reset()
                return
            }
            
            if (this.contactNameSaved) {
                return
            }

            this.getContactDetails(false)
        }
    }

    ngOnInit() {
        this.form.get('inquiry').enable()
        if (this.isDisabled) {
            this.form.get('inquiry').disable()
        }

        this.reset()
        this.fetch()

        this.subscriptions.push(
            this.form.get('inquiry').valueChanges.subscribe(inquiry => {
                if (this.watcher) {
                    clearTimeout(this.watcher)
                    this.watcher = null
                }
                
                if (this.inquiry === inquiry) { // no change?
                    return
                }

                this.watcher = setTimeout(() => {
                    this.inquiry = (inquiry || '').trim()
                    this.selectContact(this.inquiry)
                }, SEARCH_DEBOUNCE_MILLIS)
            })
        )

        this.subscriptions.push(
            this.contactQuickAddInfo.get('zipCode').valueChanges.subscribe(zipCode => {
                this.searchZipCode()
            })
        )
    }

    ngOnDestroy() {
        for (const sub of this.subscriptions) {
            sub.unsubscribe()
        }
        if (this.watcher) {
            clearTimeout(this.watcher)
        }
    }

    /**
     * Set the selected contact.
     * @param name The name of the contact the user chose from the data list or typed in
     */
    public selectContact = (name: string) => {
        if (!name || !this.contacts || (this.contacts.length <= 0)) {
            if (this.exhausted && (this.type) && (!this.ignoreType)) {
                return
            }

            this.reset()
            this.fetch()
            return
        }

        const contact = this.contacts.find((item) => item.name.toLocaleLowerCase() === name.toLocaleLowerCase())
        if (!contact) {
            this.reset()
            this.fetch()
            return
        }

        this.contactIdSelected = contact.id
        this.setContactId.emit(this.contactIdSelected)
        this.form.get('inquiry').markAsPristine()
        
        if (this.ignoreType) {
            this.assignRole(contact)
        }
        
        this.getContactDetails(false);
    }

    public displayFn(contact: any) {
        if (!contact || !contact.name) {
            return ''
        }

        return contact.name
    }

    reset() {
        this.page = 1
        this.exhausted = false
        this.displayed = 0
        this.showSaveButton = false
        this.contacts = []
    }

    fetch() {
        if (this.contactIdSaved && (this.form.get('inquiry').pristine) && this.inquiry !== '') {  // if the contact has been selected or saved, no need to search again
            return
        }

        if (this.isDisabled) { // if the user cannot select from the dropdown, no need to search
            return
        }

        if (this.loading) { // already getting next page
            return
        }

        if (this.exhausted) { // currently showing all available records?
            return
        }

        this.loading = true

        let typeParam = ''
        if (this.type && (this.type.length > 0) && !this.ignoreType) {
            typeParam = `type=${encodeURIComponent(this.type)}`
        }

        let inquiryParam = ''
        if (this.inquiry && (this.inquiry.length > 0)) {
            inquiryParam = `&q=${encodeURIComponent(this.inquiry)}`
        }

        let sortParam = ''
        if (this.sortables && (Object.keys(this.sortables).length > 0)) {
            const sorts = []
            for (const key of Object.keys(this.sortables)) {
                let direction = ''
                if (this.sortables[key] && this.sortables[key].length > 0) {
                    direction = `|${this.sortables[key]}`
                }

                sorts.push(`${key}${direction}`)
            }

            sortParam = `&sort=${sorts.join(',')}`
        }

        this.master.get(`contacts?${typeParam}&page=${this.page}${inquiryParam}${sortParam}`, (res) => {
            this.loading = false

            if (!res || !res.data) {
                this.ms.sendMessage("alert", { type: "danger", text: this.words[this.language]['apiNoResponse'] });
                return
            }

            if (res.status !== 200) {
                this.ms.sendMessage("alert", { type: "danger", text: res.data.error });
                return
            }

            this.records = res.data.records
            this.displayed = Math.min(this.records, (res.data.page * res.data.ipp))
            this.exhausted = (res.data.next === null)

            if (this.page > 1) {
                this.contacts = this.contacts.concat(res.data.contacts)
                return
            }

            this.contacts = res.data.contacts

            /*
            // If we don't have any results from the initial fetch and we are 
            // locked down by a specific type then lets open it up to all types.
            if (!this.ignoreType && typeParam && !inquiryParam && (this.contacts.length <= 0)) {
                this.ignoreType = true
                this.reset()
            }
            */
        })
    }

    public getRoles(roleSelected: string) {
        if (this.ignoreType == true) {
            return " | Roles: " + roleSelected
        }

        return null
    }

    /**
    * Handle scroll events of the list container and load 
    * the next page when avilable and the user scrolled 
    * to the bottom.
    * @param e The scroll event that trigggered this handler.
    */
    public scrolled = (e): void => {
        if (!e || !e.target) {
            return
        }

        if (this.exhausted) {
            return
        }

        if (this.loading) {
            return
        }

        const location = Math.abs(e.target.scrollHeight - e.target.clientHeight - e.target.scrollTop)

        if (location >= SCROLL_BOTTOM_OUT) {
            return
        }

        this.page = this.page + 1
        this.fetch()
    }

    public resetInformation() {
        this.contactQuickAddInfo.reset()
        this.contactQuickAddInfo.get("roles").setValue(this.type)
        this.contactQuickAddInfo.get("isIndividual").setValue("false")
        this.contactIdSaved = 0
        this.contactNameSaved = null
        this.contactIdSelected = null
        this.showSaveButton = false
        this.ignoreType = false
        this.contactCreated = false
        this.setContactId.emit(this.contactIdSelected)
    }

    public getContactDetails(open) {
        const identity = this.contactIdSelected ? this.contactIdSelected : this.contactIdSaved ? this.contactIdSaved : null
        if (!identity || isNaN(identity) || (identity <= 0)) {
            return
        }

        this.master.get(`contacts/${identity}`, res => {
            if (!res) {
                this.ms.sendMessage("alert", { type: "danger", text: this.words[this.language]['apiNoResponse'] });
            }

            if (res.status !== 200) {
                this.ms.sendMessage("alert", { type: "danger", text: res.data.error });
            }

            if (!res.data.contact) {
                return
            }

            this.form.get('inquiry').setValue(res.data.contact.name, {emitEvent: false})
            this.inquiry = res.data.contact.name

            if (!open) {
                return
            }

            this.setContactQuickAddInfo(res.data.contact)
            const btn = (document.getElementById(`createNewContactBtn_${this.type}_${this.key || ''}`) as HTMLButtonElement)      // opens the contact info
            if (btn) { 
                btn.click() 
            }
        })
    }

    public saveContactQuickAdd() {
        this.resetInformation()
        this.contactQuickAddInfo.get("companyName").setValue(this.form.get("inquiry").value)
        if (this.contactIdSelected <= 0) {
            this.createContact()
            return
        }

        this.modifyContact()
    }

    public createContact() {
        const information = this.contactQuickAddInfo.getRawValue()
        
        const id = information.id
        if (id && (id > 0)) { // not new?
            return
        }
        
        if (information.roles && (information.roles as unknown as string[]).length > 0) {
            const role = information.roles as unknown as string
            ;(information.roles as unknown as string[]) = [role]
        }

        this.contactCreated = true
        this.master.post(`contacts/quick`, { information: information }, res => {
            if (!res) {
                this.ms.sendMessage("alert", { type: "danger", text: this.words[this.language]['apiNoResponse'] });
            }

            if (res.status !== 200) {
                this.ms.sendMessage("alert", { type: "danger", text: res.data.error });
            }

            if (!res.data.contact) {
                return
            }

            this.contactIdSelected = res.data.contact['id']
            this.inquiry = information.companyName
            this.setContactId.emit(this.contactIdSelected)
            this.contactQuickAddInfo.get("id").setValue(this.contactIdSelected)
            this.contactCreated = false
        })
    }

    public modifyContact() {
        this.contactCreated = false
        if (this.contactQuickAddInfo.invalid) {
            return
        }

        const information = this.contactQuickAddInfo.getRawValue()
        if (information.roles && (information.roles as unknown as string[]).length > 0) {
            const role = information.roles as unknown as string
            ;(information.roles as unknown as string[]) = [role]
        }

        this.master.put(`contacts/quick/${this.contactIdSelected}`, { information: information }, res => {
            if (!res) {
                this.ms.sendMessage("alert", { type: "danger", text: this.words[this.language]['apiNoResponse'] });
            }

            if (res.status !== 200) {
                this.ms.sendMessage("alert", { type: "danger", text: res.data.error });
            }

            const btn = (document.getElementById(`btn-close-modal-create-new-contact_${this.type}_${this.key || ''}`) as HTMLButtonElement)
            if (btn) { btn.click() }
        })
    }

    public assignRole(contactSelected) {
        const role = this.words[this.language][this.type]
        const foundRole = contactSelected['roles'].search(role)
        if (foundRole !== -1) {
            return
        }
        this.master.post(`contacts/${contactSelected['id']}/roles`, { information: this.type }, res => {
            if (!res) {
                return;
            }

            if (res.status !== 200) {
                this.ms.sendMessage("alert", { type: "danger", text: res.data.error });
                return;
            }

        })
    }

    public setContactQuickAddInfo(contact: Object) {
        if (!contact) {
            return
        }

        this.inquiry = contact['name']
        this.contactNameSaved = contact['name']
        this.contactIdSaved = contact['id']

        this.contactQuickAddInfo.patchValue({
            id: contact['id'],
            companyName: contact['companyName'],
            firstName: contact['firstName'],
            lastName: contact['lastName'],
            middleName: contact['middleName'],
            isIndividual: contact['isIndividual'],
            address: contact['address'] ? contact['address'] : contact['contactAddresses'] && contact['contactAddresses'].length > 0 ? contact['contactAddresses'][0].address : null,
            address2: contact['address2'] ? contact['address2'] : contact['contactAddresses'] && contact['contactAddresses'].length > 0 ? contact['contactAddresses'][0].address2 : null,
            city: contact['city'] ? contact['city'] : contact['contactAddresses'] && contact['contactAddresses'].length > 0 ? contact['contactAddresses'][0].city : null,
            state: contact['state'] ? contact['state'] : contact['contactAddresses'] && contact['contactAddresses'].length > 0 ? contact['contactAddresses'][0].state : null,
            zipCode: contact['zipCode'] ? contact['zipCode'] : contact['contactAddresses'] && contact['contactAddresses'].length > 0 ? contact['contactAddresses'][0].zipCode : null,
            county: contact['county'] ? contact['county'] : contact['contactAddresses'] && contact['contactAddresses'].length > 0 ? contact['contactAddresses'][0].county : null,
            country: contact['country'] ? contact['country'] : contact['contactAddresses'] && contact['contactAddresses'].length > 0 ? contact['contactAddresses'][0].country : null,
            phoneNumber: contact['phoneNumber'] ? contact['phoneNumber'] : this.filterForCommunication(contact, 'phone'),
            emailAddress: contact['emailAddress'] ? contact['emailAddress'] : this.filterForCommunication(contact, 'email'),
            taxPayerNumber: contact['taxPayerNumber'],
            roles: contact['roles'],
        })
    }

    filterForCommunication(contact, searchFor): any[] {
        let relationship = contact.contactCommunications ? contact.contactCommunications : null
        if (!relationship || relationship.length == 0) { return null }
        const returnedValue = relationship.find(comms => comms.type === searchFor);
        if (typeof returnedValue !== 'undefined') {
            return returnedValue.value
        }
        return null
    }

    public searchZipCode(): void {
        const code = this.contactQuickAddInfo.get('zipCode').value
        if (!code || code.length < MIN_SEARCH_CHARS) {
            this.clearZipCode()
            return
        }

        if (this.zipInquiry && (code === this.zipInquiry)) { // hasn't changed?
            return
        }

        clearTimeout(this.zipWatcher)

        this.zipWatcher = setTimeout(() => {
            this.zipCodeList = []
            this.zipInquiry = code

            this.master.get(`readLocationByZipcode?zipcode=${code}`, res => {
                if (!res) {
                    this.ms.sendMessage("alert", { type: "danger", text: this.words[this.language]['apiNoResponse'] })
                    return
                }

                if (res.status !== 200) {
                    return
                }

                this.zipCodeList = res.data.locations

                // autocomplete
                if ((code.length >= 5) && (this.zipCodeList.length === 1)) {
                    this.selectZipCode(this.zipCodeList[0])
                }
            })
        }, SEARCH_DEBOUNCE_MILLIS)
    }

    // set the address exist
    public selectZipCode = (address: Object): void => {
        if (!address || !this.contactQuickAddInfo) {
            return
        }

        this.contactQuickAddInfo.get('city').setValue(address['city'])
        this.contactQuickAddInfo.get('state').setValue(address['state'])
        this.contactQuickAddInfo.get('county').setValue(address['county'])
        this.contactQuickAddInfo.get('zipCode').setValue(address['zipcode'])
        this.contactQuickAddInfo.get('country').setValue('United States')

        this.zipCodeList = []
    }

    // set the address exist
    public clearZipCode() {
        this.contactQuickAddInfo.get('city').setValue(null)
        this.contactQuickAddInfo.get('state').setValue(null)
        this.contactQuickAddInfo.get('county').setValue(null)
        this.contactQuickAddInfo.get('country').setValue(null)
        this.zipCodeList = []
    }
    /*
    todo: get types of identifications
    */
    public getTypes = (): void => {
        this.master.get('collections/contacts/identificationTypes', res => {
            if (!res) {
                this.ms.sendMessage("alert", { type: "danger", text: this.words[this.language]['apiNoResponse'] });
            }

            if (res.status !== 200) {
                this.ms.sendMessage("alert", { type: "danger", text: res.data.error });
            }
            this.types = res.data.IdentificationTypes
        })
    }

    public showSaveAndUseButton() {
        if (this.typesWithNoSetup.includes(this.type)) {
            return true
        }

        return false
    }

}

